import { defineComponent as _defineComponent } from 'vue'
import {computed, inject, Ref, ref, watch} from "vue";
import {debounce} from "@/utils/Helpers";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {useTranslations} from "@/hooks/useTranslations";
import FreeTextPlaceholder from "@/models/NutritionLabels/FreeTextPlaceholder";
import {FreeTextAttributeType} from "@/models/NutritionLabels/v2/enums/FreeTextAttributeType";
import {splitLanguages} from "@/models/NutritionLabels/v2/Helpers";
import {vSelectOptionBase} from "@/typings/common";
import {isSupplementLabelTemplate} from "@/utils/exportLabelRules";


export default /*#__PURE__*/_defineComponent({
  __name: 'FreeTextFieldsConfiguration',
  props: {
    shouldExpand: { type: Boolean },
    language: null,
    freeTextPlaceholders: null,
    selectedLabelType: null,
    country: { default: null },
    isProductFactsTableEnabled: { type: Boolean, default: false },
    stepLabel: { default: "3" }
  },
  emits: [
  "update:freeTextPlaceholders",
  "update:isProductFactsTableEnabled",
  "fetch-preview"
],
  setup(__props: any, { emit }) {

const props = __props as { shouldExpand: boolean, language: keyof typeof LabelLanguage, freeTextPlaceholders: FreeTextPlaceholder[], selectedLabelType: vSelectOptionBase<string>, country: string | null, isProductFactsTableEnabled: boolean, stepLabel: string };

const fetchPreview: () => void = inject(
  "bopFetchPreview",
  () => console.warn("No fetch preview provided")
);

// Props


const translations = useTranslations();

// Emits


// Local copy to avoid mutating props directly
const localPlaceholders: Ref<FreeTextPlaceholder[]> = ref(structuredClone(props.freeTextPlaceholders) || []);
const isPftModeEnabled = ref(!!props.isProductFactsTableEnabled);

const PFT_ONLY_FIELDS = new Set<FreeTextAttributeType>([
  FreeTextAttributeType.MEDICINAL_INGREDIENTS_HEADER,
  FreeTextAttributeType.PRODUCT_LICENCE_HOLDER_OR_IMPORTER,
  FreeTextAttributeType.PRODUCT_LICENCE_HOLDER_CONTACT_INFORMATION,
  FreeTextAttributeType.RECOMMENDED_USE_OR_PURPOSE,
  FreeTextAttributeType.RECOMMENDED_ROUTE_OF_ADMINISTRATION,
  FreeTextAttributeType.DOSE,
  FreeTextAttributeType.DURATION_OF_USE,
  FreeTextAttributeType.WARNINGS,
  FreeTextAttributeType.CONTAINS_ASPARTAME,
  FreeTextAttributeType.DO_NOT_USE,
  FreeTextAttributeType.ASK_HEALTH_CARE_PRACTITIONER,
  FreeTextAttributeType.KNOWN_ADVERSE_REACTIONS,
  FreeTextAttributeType.RISK_INFORMATION,
  FreeTextAttributeType.DIRECTIONS,
  FreeTextAttributeType.STORAGE_INFORMATION,
  FreeTextAttributeType.LOT_NUMBER,
  FreeTextAttributeType.EXPIRY_DATE,
  FreeTextAttributeType.QUESTIONS_OR_CONTACT,
]);

const isCanadaSupplementLabel = computed(() => {
  return props.country === "Canada"
    && isSupplementLabelTemplate(props.selectedLabelType?.value, null);
});

const hasPftFields = computed(() => {
  return localPlaceholders.value.some((placeholder) => PFT_ONLY_FIELDS.has(placeholder.displayText));
});

const shouldShowPftModeToggle = computed(() => {
  return isCanadaSupplementLabel.value && hasPftFields.value;
});

const displayedPlaceholders = computed(() => {
  if (!shouldShowPftModeToggle.value || isPftModeEnabled.value) {
    return localPlaceholders.value;
  }

  return localPlaceholders.value.filter((placeholder) => !PFT_ONLY_FIELDS.has(placeholder.displayText));
});

const labelsToHideSecondaryLangServingSize = ["CannabisLabel"];
const shouldHideSecondaryLanguageServingSize = computed(
  () => labelsToHideSecondaryLangServingSize.includes(props.selectedLabelType.value) && isBilingual.value
);

const isBilingual = computed(() => {
  const language = LabelLanguage[props.language];
  return language === LabelLanguage.BilingualEnglishFrenchEnglishFirst
    || language === LabelLanguage.BilingualEnglishFrenchFrenchFirst
    || language === LabelLanguage.BilingualEnglishSpanishEnglishFirst
    || language === LabelLanguage.BilingualEnglishSpanishSpanishFirst;
});

const languageHeaders = computed(() => {
  return splitLanguages(LabelLanguage[props.language]);
});

function shouldShowLanguageLabel(placeholder: FreeTextPlaceholder) {
  if (
    labelsToHideSecondaryLangServingSize.includes(props.selectedLabelType.value)
    && placeholder.displayText === 'HouseholdMeasurement'
  ) {
    return false;
  }

  return isBilingual.value;
}
function shouldDisplayInput(placeholder: FreeTextPlaceholder, languageIndex: number) {
  if (placeholder.displayText !== 'HouseholdMeasurement') {
    return true;
  }

  if (placeholder.displayText == 'HouseholdMeasurement' && !shouldHideSecondaryLanguageServingSize.value) {
    return true;
  }

  return placeholder.displayText == 'HouseholdMeasurement'
    && shouldHideSecondaryLanguageServingSize.value
    && languageIndex < 1;
}

function getInputType(placeholder: FreeTextAttributeType) {
  switch (placeholder) {
  case FreeTextAttributeType.CAUTIONARY_MESSAGES:
    return "textarea";
  case FreeTextAttributeType.MANUFACTURER_NAME_AND_ADDRESS:
    return "textarea";
  case FreeTextAttributeType.PREPARATION_INSTRUCTIONS:
    return "textarea";
  case FreeTextAttributeType.SERVINGS_PER_CONTAINER:
  case FreeTextAttributeType.PORTIONS_PER_CONTAINER:
    return "text";
  default:
    return "text";
  }
}

function getLanguageName(language: LabelLanguage) {
  return translations.value.get(LabelLanguage[language]);
}

function shouldUppercaseFieldLabel(placeholder: FreeTextAttributeType) {
  return [
    FreeTextAttributeType.CAUTIONARY_MESSAGES,
    FreeTextAttributeType.ALLERGY_ADVICE,
    FreeTextAttributeType.QUANTITATIVE_DECLARATION_OF_INGREDIENTS,
    FreeTextAttributeType.NET_QUANTITY,
    FreeTextAttributeType.MANUFACTURER_NAME_AND_ADDRESS,
    FreeTextAttributeType.COUNTRY_OF_ORIGIN,
    FreeTextAttributeType.PREPARATION_INSTRUCTIONS,
    FreeTextAttributeType.STORAGE_INSTRUCTIONS,
    FreeTextAttributeType.FOOD_ADDITIVES,
    FreeTextAttributeType.DURABILITY_INDICATION,
    FreeTextAttributeType.ALCOHOLIC_STRENGTH_BY_VOLUME
  ].includes(placeholder);
}

// Watch for parent changes
watch(
  () => props.freeTextPlaceholders,
  (newVal) => {
    const incomingPlaceholders = structuredClone(newVal) || [];
    const existingPlaceholdersByType = new Map(
      localPlaceholders.value.map((placeholder) => [placeholder.displayText, placeholder])
    );

    for (const placeholder of incomingPlaceholders) {
      existingPlaceholdersByType.set(placeholder.displayText, placeholder);
    }

    localPlaceholders.value = Array.from(existingPlaceholdersByType.values()).sort((left, right) => left.id - right.id);
  },
  { deep: true }
);

watch(
  () => shouldShowPftModeToggle.value,
  (shouldShow) => {
    if (!shouldShow) {
      isPftModeEnabled.value = false;
      emit("update:isProductFactsTableEnabled", false);
    }
  },
  { immediate: true }
);

watch(
  () => props.isProductFactsTableEnabled,
  (isEnabled) => {
    isPftModeEnabled.value = !!isEnabled;
  }
);

// On input change, emit updates and trigger debounced preview
function onInputChange() {
  const normalized = normalizeFreeTextFields(displayedPlaceholders.value);

  emit("update:freeTextPlaceholders", normalized);
  // Debounced preview event
  debounce(fetchPreview, 1000, "back-of-package-config-label-update");
}

function onPftModeChange() {
  emit("update:isProductFactsTableEnabled", isPftModeEnabled.value);

  const normalized = normalizeFreeTextFields(displayedPlaceholders.value);

  emit("update:freeTextPlaceholders", normalized);
  debounce(fetchPreview, 1000, "back-of-package-config-label-update");
}

function normalizeFreeTextFields(
  placeholders: FreeTextPlaceholder[]
): FreeTextPlaceholder[] {
  return placeholders.map((p) => ({
    ...p,
    // Preserve language-indexed slots. Removing empty values here causes
    // bilingual content to shift between inputs when one side is cleared.
    content: [...p.content]
  }));
}

return { __sfc: true,fetchPreview, props, translations, emit, localPlaceholders, isPftModeEnabled, PFT_ONLY_FIELDS, isCanadaSupplementLabel, hasPftFields, shouldShowPftModeToggle, displayedPlaceholders, labelsToHideSecondaryLangServingSize, shouldHideSecondaryLanguageServingSize, isBilingual, languageHeaders, shouldShowLanguageLabel, shouldDisplayInput, getInputType, getLanguageName, shouldUppercaseFieldLabel, onInputChange, onPftModeChange, normalizeFreeTextFields, CollapsableCard }
}

})